import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Alert,
  Skeleton,
  Collapse,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  useMediaQuery,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Key as KeyIcon,
  Close as CloseIcon,
  Palette as PaletteIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SettingsBrightnessIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useAppTheme, THEME_OPTIONS } from '../contexts/ThemeContext';
import { formatDate } from '../utils/formatDate';

// Profile update validation schema
const profileSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Enter a valid email').required('Email is required')
});

// Password change validation schema
const passwordSchema = Yup.object({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match')
    .required('Please confirm your new password')
});

export default function Profile() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { user, updateProfile, changePassword, fetchUserProfile, logout } = useAuth();
  const { themePreference, changeTheme } = useAppTheme();
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const queryClient = useQueryClient();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showPasswordAlert, setShowPasswordAlert] = useState(false);
  const [passwordAlertMessage, setPasswordAlertMessage] = useState('');
  const [passwordAlertType, setPasswordAlertType] = useState('success');

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Profile update mutation
  const profileMutation = useMutation({
    mutationFn: async (profileData) => {
      return await updateProfile(profileData);
    },
    onSuccess: () => {
      setAlertMessage('Profile updated successfully!');
      setShowAlert(true);
      // Invalidate users query to refresh the All Users page
      queryClient.invalidateQueries({ queryKey: ['users'] });
    }
  });

  // Password change mutation
  const passwordMutation = useMutation({
    mutationFn: async (passwordData) => {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      if (!result.success) {
        throw new Error(result.error || 'Current password is incorrect');
      }
      return result;
    },
    onSuccess: () => {
      setPasswordAlertType('success');
      setPasswordAlertMessage('Password changed successfully! You will be logged out.');
      setShowPasswordAlert(true);
      // Clear form
      passwordFormik.resetForm();

      // Add a small delay before logout to allow the user to see the success message
      setTimeout(() => {
        logout();
      }, 1500);
    },
    onError: (error) => {
      setPasswordAlertType('error');
      setPasswordAlertMessage(`Failed to change password: ${error.message}`);
      setShowPasswordAlert(true);
      // Clear form
      passwordFormik.resetForm();
    }
  });

  // Profile form
  const profileFormik = useFormik({
    initialValues: {
      firstName: user?.firstName || user?.first_name || '',
      lastName: user?.lastName || user?.last_name || '',
      email: user?.email || ''
    },
    validationSchema: profileSchema,
    onSubmit: async (values) => {
      await profileMutation.mutateAsync(values);
    },
    enableReinitialize: true
  });

  // Function to check if password form is valid
  const isPasswordFormValid = (values, errors) => {
    return (
      values.currentPassword &&
      values.newPassword &&
      values.confirmPassword &&
      values.newPassword === values.confirmPassword &&
      values.newPassword.length >= 8 &&
      Object.keys(errors).length === 0
    );
  };

  // Password form
  const passwordFormik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      await passwordMutation.mutateAsync(values);
    },
    validateOnMount: true,
    validateOnChange: true
  });

  return (
    <Box>
      <PageHeader
        title="My Profile"
        subtitle="Manage your account information"
      />

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: 10,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
              }}>
                <PersonIcon sx={{ mr: 1 }} />
                Profile Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <form onSubmit={profileFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    {profileMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="firstName"
                        name="firstName"
                        label="First Name"
                        value={profileFormik.values.firstName}
                        onChange={profileFormik.handleChange}
                        onBlur={profileFormik.handleBlur}
                        error={profileFormik.touched.firstName && Boolean(profileFormik.errors.firstName)}
                        helperText={profileFormik.touched.firstName && profileFormik.errors.firstName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {profileMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="lastName"
                        name="lastName"
                        label="Last Name"
                        value={profileFormik.values.lastName}
                        onChange={profileFormik.handleChange}
                        onBlur={profileFormik.handleBlur}
                        error={profileFormik.touched.lastName && Boolean(profileFormik.errors.lastName)}
                        helperText={profileFormik.touched.lastName && profileFormik.errors.lastName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {profileMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email Address"
                        value={profileFormik.values.email}
                        onChange={profileFormik.handleChange}
                        onBlur={profileFormik.handleBlur}
                        error={profileFormik.touched.email && Boolean(profileFormik.errors.email)}
                        helperText={profileFormik.touched.email && profileFormik.errors.email}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {profileMutation.isPending ? (
                        <Skeleton variant="rectangular" width={140} height={36} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={!profileFormik.dirty}
                          sx={{
                            position: 'relative',
                            mt: 2,
                            '&.Mui-disabled': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                              cursor: 'not-allowed'
                            }
                          }}
                        >
                          {profileFormik.isSubmitting ? (
                            <>
                              <Box
                                component="span"
                                sx={{
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <CircularProgress size={20} color="inherit" />
                                Saving...
                              </Box>
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>

              <Collapse in={showAlert}>
                <Alert
                  severity="success"
                  sx={{
                    mt: 2,
                    '& .MuiAlert-action': {
                      alignItems: 'center'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setShowAlert(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {alertMessage}
                </Alert>
              </Collapse>

              {profileMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to update profile: {profileMutation.error?.message || 'Unknown error'}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              boxShadow: 10,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
              }}>
                <KeyIcon sx={{ mr: 1 }} />
                Change Password
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <form onSubmit={passwordFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {passwordMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="currentPassword"
                        name="currentPassword"
                        label="Current Password"
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={passwordFormik.values.currentPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={passwordFormik.touched.currentPassword && Boolean(passwordFormik.errors.currentPassword)}
                        helperText={passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                edge="end"
                              >
                                {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {passwordMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="newPassword"
                        name="newPassword"
                        label="New Password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordFormik.values.newPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={passwordFormik.touched.newPassword && Boolean(passwordFormik.errors.newPassword)}
                        helperText={passwordFormik.touched.newPassword && passwordFormik.errors.newPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                              >
                                {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {passwordMutation.isPending ? (
                      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
                    ) : (
                      <TextField
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        label="Confirm New Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={passwordFormik.values.confirmPassword}
                        onChange={passwordFormik.handleChange}
                        onBlur={passwordFormik.handleBlur}
                        error={passwordFormik.touched.confirmPassword && Boolean(passwordFormik.errors.confirmPassword)}
                        helperText={passwordFormik.touched.confirmPassword && passwordFormik.errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'
                            },
                            '&:hover fieldset': {
                              borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                            }
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {passwordMutation.isPending ? (
                        <Skeleton variant="rectangular" width={160} height={36} sx={{ borderRadius: 1 }} />
                      ) : (
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<KeyIcon />}
                          disabled={!isPasswordFormValid(passwordFormik.values, passwordFormik.errors)}
                          sx={{
                            position: 'relative',
                            mt: 2,
                            '&.Mui-disabled': {
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                              color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                              cursor: 'not-allowed'
                            }
                          }}
                        >
                          {passwordFormik.isSubmitting ? (
                            <>
                              <Box
                                component="span"
                                sx={{
                                  position: 'absolute',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}
                              >
                                <CircularProgress size={20} color="inherit" />
                                Updating...
                              </Box>
                            </>
                          ) : (
                            'Change Password'
                          )}
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>

              <Collapse in={showPasswordAlert}>
                <Alert
                  severity={passwordAlertType}
                  sx={{
                    mt: 2,
                    '& .MuiAlert-action': {
                      alignItems: 'center'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setShowPasswordAlert(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {passwordAlertMessage}
                </Alert>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        {/* Theme Settings */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              boxShadow: 10,
              overflow: 'hidden'
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
              }}>
                <PaletteIcon sx={{ mr: 1 }} />
                Theme Settings
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup
                  value={themePreference}
                  onChange={(e) => changeTheme(e.target.value)}
                >
                  <FormControlLabel
                    value={THEME_OPTIONS.LIGHT}
                    control={
                      <Radio
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
                          '&.Mui-checked': {
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.main
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LightModeIcon sx={{ mr: 1, color: 'warning.main' }} />
                        <Typography>Light Theme (Default)</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={THEME_OPTIONS.DARK}
                    control={
                      <Radio
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
                          '&.Mui-checked': {
                            color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.main
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DarkModeIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography>Dark Theme</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={THEME_OPTIONS.SYSTEM}
                    control={
                      <Radio
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
                          '&.Mui-checked': {
                            color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.main
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SettingsBrightnessIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography>System Default</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Box mt={2}>
                <Alert
                  severity="info"
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(7, 177, 255, 0.1)' : undefined,
                    color: theme.palette.mode === 'dark' ? theme.palette.info.light : undefined,
                    '& .MuiAlert-icon': {
                      color: theme.palette.mode === 'dark' ? theme.palette.info.light : undefined
                    }
                  }}
                >
                  System Default will automatically switch between light and dark theme based on your system settings.
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              boxShadow: 10,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
              }}>
                <PersonIcon sx={{ mr: 1 }} />
                Account Information
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={isMobile ? 3 : 2}>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User ID
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {user?.id || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Role
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{
                      textTransform: 'capitalize',
                      fontWeight: user?.role === 'admin' ? 'bold' : 'normal',
                      color: user?.role === 'admin' ? (theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main) : 'text.primary'
                    }}
                  >
                    {user?.role || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Account Created
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(user?.created_at || user?.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Last Login
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formatDate(user?.last_login || user?.lastLogin)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
